import { useCallback, useRef } from 'react';

interface DividerHandleProps {
  orientamento: 'verticale' | 'orizzontale';
  /** Dimensione corrente in px del pannello che precede la maniglia. */
  valore: number;
  min: number;
  max: number;
  /** Riceve il nuovo valore gia' clampato (drag o frecce tastiera). */
  onChange: (v: number) => void;
  /** Doppio click: torna alla misura di default. */
  onReset: () => void;
  label: string;
}

/**
 * Maniglia di ridimensionamento senza dipendenze: pointer capture sul
 * divisore, delta applicato al valore di inizio drag, clamp min/max.
 * Accessibile: role separator, frecce tastiera a passi di 16px.
 */
export function DividerHandle({
  orientamento,
  valore,
  min,
  max,
  onChange,
  onReset,
  label,
}: DividerHandleProps) {
  const inizio = useRef<{ pos: number; valore: number } | null>(null);
  const verticale = orientamento === 'verticale';

  const clamp = useCallback(
    (v: number) => Math.min(max, Math.max(min, v)),
    [min, max]
  );

  // Il drag parte dalla misura RENDERIZZATA del pannello che precede (non
  // dal valore salvato): quando il pannello e' compresso dal flex (finestra
  // piccola, valore salvato > spazio reale) il valore salvato creerebbe una
  // zona morta in cui il trascinamento non muove nulla.
  const misuraRenderizzata = (el: HTMLElement): number => {
    const prev = el.previousElementSibling;
    if (!(prev instanceof HTMLElement)) return valore;
    const r = prev.getBoundingClientRect();
    return verticale ? r.width : r.height;
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    inizio.current = {
      pos: verticale ? e.clientX : e.clientY,
      valore: misuraRenderizzata(e.currentTarget),
    };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!inizio.current) return;
    const delta = (verticale ? e.clientX : e.clientY) - inizio.current.pos;
    onChange(clamp(inizio.current.valore + delta));
  };
  const releasePointer = (e: React.PointerEvent<HTMLDivElement>) => {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // pointercancel potrebbe aver già rilasciato la capture
    }
    inizio.current = null;
  };
  const onPointerUp = releasePointer;
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const meno = verticale ? 'ArrowLeft' : 'ArrowUp';
    const piu = verticale ? 'ArrowRight' : 'ArrowDown';
    const base = misuraRenderizzata(e.currentTarget);
    if (e.key === meno) {
      e.preventDefault();
      onChange(clamp(base - 16));
    } else if (e.key === piu) {
      e.preventDefault();
      onChange(clamp(base + 16));
    }
  };

  return (
    <div
      role="separator"
      tabIndex={0}
      aria-orientation={verticale ? 'vertical' : 'horizontal'}
      aria-label={label}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={releasePointer}
      onDoubleClick={onReset}
      onKeyDown={onKeyDown}
      className={
        verticale
          ? 'w-1.5 shrink-0 cursor-col-resize bg-border/50 transition-colors hover:bg-primary/40 focus-visible:bg-primary/40'
          : 'h-1.5 shrink-0 cursor-row-resize bg-border/50 transition-colors hover:bg-primary/40 focus-visible:bg-primary/40'
      }
    />
  );
}
